import axiosPkg, { AxiosError, type AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { keycloak } from "./keycloak";
import { toast } from "sonner";

const axios = axiosPkg.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  withCredentials: true,
});

axiosRetry(axios, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    if (axiosRetry.isNetworkError(error)) {
      return true;
    } else if (
      error.response &&
      (error.response.status === 500 || error.response.status === 401)
    ) {
      return true;
    } else {
      return false;
    }
  },
  async onRetry(retryCount, error, requestConfig) {
    if (retryCount === 1 && error.response && error.response.status === 401) {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        requestConfig.headers = {
          ...requestConfig.headers,
          Authorization: `Bearer ${keycloak.token ?? ""}`,
        };
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${keycloak.token ?? ""}`;
      }
    }

    if (retryCount === 2 && error.response && error.response.status === 401) {
      toast.error("Your session has expired", { richColors: true });
      setTimeout(() => {
        keycloak.logout();
      }, 1500);
    }
  },
});

axios.interceptors.response.use(
  async (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 429) {
      toast.error("Too many requests", { richColors: true });
    }
    return Promise.reject(error);
  }
);

function handleAxiosError(
  error: unknown,
  callback: (response?: AxiosResponse) => void
) {
  const err = error;
  if (err instanceof AxiosError) {
    if (err.response) {
      callback(err.response);
    } else {
      callback();
    }
  } else {
    callback();
  }
}

export { axios, handleAxiosError };
